import React, { useEffect, useRef, useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import TwygsEditorNodes from '../nodes/TwygsEditorNodes';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { CommentableParagraphNode } from '../nodes/CommentableParagraphNode';
import { ParagraphNode } from 'lexical';
import getTheme from '../themes/TwygsEditorTheme';
import { CommentSidebar } from '../components/CommentsSidebar';
import ViewerContextMenuPlugin from '../plugins/ViewerContextMenuPlugin';
import TwygsBotSidebar from '../components/TwygsBotSidebar';
import FactCheckSidebar from '../components/FactCheckSidebar';
import { useAtom } from 'jotai';
import { profileAtom } from '../../Settings/atom';
import PollPlugin from '../plugins/PollPlugin';
import ExcalidrawPlugin from '../plugins/ExcalidrawPlugin';
import TableOfContentsViewerPlugin from '../plugins/TableOfContentsViewerPlugin';
import AdPlacementPlugin from '../plugins/AdInsertionPlugin';
import { useSearchParams } from 'react-router-dom';
import ForceReadOnlyPlugin from '../plugins/ForceReadOnlyPlugin';
import { ReactionModalPlugin } from '../plugins/ReactionModalPlugin';
import ReactionsPlugin from '../plugins/ReactionsPlugin';
import { getInitializationFactCheck } from '../../../apis/article-viewer';
import { CommentableListNode } from '../nodes/CommentableListNode';
import { ListNode } from '@lexical/list';
import './article-viewer.css';
import '../ui/ContentEditable.css';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import CommentsPlugin from '../plugins/CommentsPlugin';
// GLOBAL VARIABLES FOR LEXICAL SCOPE
export const fetchedVotePollIds = [];

const LexicalViewer = ({ showSensitiveContentCard, content, article, articleId }) => {
  const [searchParams] = useSearchParams();
  const [profile] = useAtom(profileAtom);
  const newFontViewEnabled = searchParams.get('newfontview') === 'true';
  const fontFamily = newFontViewEnabled ? "'Source Serif 4', Georgia, serif" : '';
  const editorStateRef = useRef(null);
  const initialEditorState = content
    ? JSON.stringify(content?.editorState || { root: content?.root })
    : undefined;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeNodeId, setActiveNodeId] = useState<string | null>(null);
  const [notificationCommentId, setNotificationCommentId] = useState<string | null>(null);
  const [activeNodeText, setActiveNodeText] = useState<string>('');
  const [activeMediaInfo, setActiveMediaInfo] = useState<any>(null);
  const [isTwygsBotOpen, setIsTwygsBotOpen] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const [isFactCheckOpen, setIsFactCheckOpen] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [factChecks, setFactChecks] = useState([]);
  const [_floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null);

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  const handleOpenComments = (e: CustomEvent) => {
    setActiveNodeId(e.detail.nodeId);
    setActiveNodeText(e.detail.nodeText);
    setActiveMediaInfo(e.detail.mediaInfo || null);
    setCommentCount(e.detail.commentCount);
    setSidebarOpen(true);

    // Prevent background scrolling when sidebar is open
    document.body.style.overflow = 'hidden';
  };

  useEffect(() => {
    if (window) {
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
    }
  }, []);

  useEffect(() => {
    return () => {
      fetchedVotePollIds.length = 0;
    };
  }, []);

  useEffect(() => {
    document.addEventListener('openComments', handleOpenComments as EventListener);
    return () => {
      document.removeEventListener('openComments', handleOpenComments as EventListener);
    };
  }, []);

  useEffect(() => {
    const handleCommentCountUpdatedWrapper = (e: CustomEvent) => {
      // Update if this is for the currently active node AND the sidebar is open
      if (e.detail.nodeId === activeNodeId && sidebarOpen) {
        setCommentCount(e.detail.newCommentCount);
      }
    };

    window.addEventListener(
      'commentCountUpdated',
      handleCommentCountUpdatedWrapper as EventListener
    );

    return () => {
      window.removeEventListener(
        'commentCountUpdated',
        handleCommentCountUpdatedWrapper as EventListener
      );
    };
  }, [activeNodeId, sidebarOpen]);

  useEffect(() => {
    if (profile?.id) {
      const getUserFactCheck = async () => {
        try {
          const response = await getInitializationFactCheck({
            articleId
          });

          setFactChecks(response);
        } catch (error) {
          console.error('Error getting initialization fact check:', error);
        }
      };
      getUserFactCheck();
    }
  }, []);

  // handle notification redirect action
  useEffect(() => {
    switch (searchParams.get('nav')) {
      case 'paragraph': {
        const paragraphId = searchParams.get('id');
        const commentId = searchParams.get('commentId');

        if (!paragraphId || !commentId) return;

        setNotificationCommentId(commentId);

        const getParagraphTextFromJSON = (editorStateJSON: any, targetParagraphId: string) => {
          let paragraphText = null;
          let commentCountFromNode = 0;
          const traverseNode = (node: any) => {
            if (node.nodeId === targetParagraphId) {
              commentCountFromNode = node?.paragraph?.comment?.commentCount;
              node.children.forEach((childNode: any) => {
                if (childNode.type === 'text') {
                  paragraphText = childNode.text;
                }
              });
              return;
            }

            if (node.children) {
              node.children.forEach(traverseNode);
            }
          };
          traverseNode(editorStateJSON);
          return { paragraphText, commentCountFromNode };
        };
        const data = getParagraphTextFromJSON(content?.editorState?.root, paragraphId);

        setActiveNodeId(paragraphId);
        setActiveNodeText(data.paragraphText);

        handleOpenComments({
          detail: {
            nodeId: paragraphId,
            nodeText: data.paragraphText,
            commentCount: data.commentCountFromNode
          }
        } as any);

        break;
      }
      case 'general': {
        // TODO: implement direct to general
        break;
      }
      default: {
        break;
      }
    }
  }, []);

  const initialConfig = {
    editorState: initialEditorState,
    namespace: 'Twygs-Viewer',
    editable: false,
    nodes: [
      ...TwygsEditorNodes,
      {
        replace: ParagraphNode,
        with: () => new CommentableParagraphNode()
      },
      // {
      //   replace: HeadingNode,
      //   with: (node) => new CommentableHeadingNode(undefined, undefined, 0, 0, {}, node.__tag)
      // },
      {
        replace: ListNode,
        with: (node) => {
          if (node instanceof ListNode) {
            return new CommentableListNode(node.getListType(), node.getStart());
          }
          return new CommentableListNode();
        }
      }
    ],
    onError: (error) => {
      console.error('Lexical Viewer Error:', error);
    },
    theme: {
      ...getTheme(false)
    }
  };

  return (
    <>
      <LexicalComposer initialConfig={initialConfig as any}>
        <div className="editor-shell">
          <div className="editor-container">
            <div className="editor-inner">
              <RichTextPlugin
                contentEditable={
                  <div ref={onRef}>
                    <ContentEditable
                      className={`ContentEditable__root viewer-input ${showSensitiveContentCard && !article?.isShowSensitive && 'blur-lg'}`}
                      style={{
                        outline: 'none',
                        cursor: 'default',
                        userSelect: 'text',
                        fontFamily: fontFamily,
                        textAlign: 'justify'
                      }}
                    />
                  </div>
                }
                placeholder={null}
                ErrorBoundary={LexicalErrorBoundary}
              />
              <OnChangePlugin
                onChange={(editorState) => {
                  editorStateRef.current = editorState;
                }}
              />
              <ListPlugin />
              <LinkPlugin />
              <ClickableLinkPlugin disabled={false} newTab />
              <TabIndentationPlugin />
              <HistoryPlugin />
              <MarkdownShortcutPlugin />
              <PollPlugin />
              <AdPlacementPlugin articleId={article?.id} />
              <TablePlugin hasHorizontalScroll={true} />
              <ReactionsPlugin article={article} articleId={articleId} />
              <CommentsPlugin />
              <ExcalidrawPlugin />
              <ForceReadOnlyPlugin />
              <ReactionModalPlugin articleId={articleId} />
              <ViewerContextMenuPlugin
                setSelectedText={setSelectedText}
                menuPosition={menuPosition}
                setMenuPosition={setMenuPosition}
                setIsFactCheckOpen={setIsFactCheckOpen}
                setSidebarOpen={setSidebarOpen}
                setActiveNodeId={setActiveNodeId}
                setActiveNodeText={setActiveNodeText}
                setCommentCount={setCommentCount}
                content={content}
              />
              <TableOfContentsViewerPlugin
                setIsTwygsBotOpen={setIsTwygsBotOpen}
                setIsFactCheckOpen={setIsFactCheckOpen}
              />
            </div>
          </div>
        </div>
      </LexicalComposer>
      <CommentSidebar
        isOpen={sidebarOpen}
        notificationCommentId={notificationCommentId}
        onClose={() => {
          fetchedVotePollIds.length = 0;
          setSidebarOpen(false);
          setSelectedText('');

          // Restore background scrolling when sidebar is closed
          document.body.style.overflow = 'auto';
        }}
        article={article}
        articleId={articleId}
        nodeId={activeNodeId}
        nodeText={activeNodeText}
        mediaInfo={activeMediaInfo}
        selectedText={selectedText}
        setSelectedText={setSelectedText}
        currentUser={{
          id: profile.id,
          firstName: profile.firstName || '',
          name: profile.firstName,
          imgUrl: profile.imgUrl,
          isVerifiedAuthor: profile.isVerifiedAuthor,
          username: profile.username
        }}
        commentCount={commentCount}
      />
      <TwygsBotSidebar
        isOpen={isTwygsBotOpen}
        onClose={() => setIsTwygsBotOpen(false)}
        articleId={articleId}
      />
      <FactCheckSidebar
        isOpen={isFactCheckOpen}
        onClose={() => {
          setIsFactCheckOpen(false);
          setSelectedText('');
        }}
        articleId={articleId}
        selectedText={selectedText}
        initialFactChecks={factChecks}
      />
    </>
  );
};

export default LexicalViewer;
